import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import * as THREE from 'three'
import {
  getTools,
  getCube,
  generateAreaPositions,
  SCENE_DEFAULTS,
  updateLights,
  lightPresets
} from '@webgamekit/threejs'
import type {
  SetupConfig,
  CoordinateTuple,
  AreaConfig,
  OnProgress,
  LightPreset,
  LightsConfig
} from '@webgamekit/threejs'
import { createTimelineManager } from '@webgamekit/animation'
import { usePanelsStore } from '@/stores/panels'
import { useViewPanelsStore } from '@/stores/viewPanels'
import { useDebugSceneStore } from '@/stores/debugScene'
import type { SceneElement } from '@/stores/debugScene'
import { useElementPropertiesStore } from '@/stores/elementProperties'
import { useTextureGroupsStore } from '@/stores/textureGroups'
import type { TextureGroup, GroupConfig } from '@/stores/textureGroups'
import { addGroupMeshes, removeGroupMeshes } from '@/utils/groupMeshes'
import {
  toggleObjectVisibility,
  replaceGeometry,
  setSceneEnvironment
} from '@/utils/threeObjectUpdaters'
import { groundSchema, configControls } from '@/views/Tools/SceneEditor/config'
import { registerCameraProperties as registerCameraPropertiesShared } from '@/utils/cameraProperties'
import { createLightTransitionPlayer } from '@/utils/lightTransition'
import { numberDuplicateNames } from '@/utils/elementNames'
import { LIGHT_ELEMENT_NAMES, readLightsFromScene } from '@/utils/sceneLights'
import { getNestedValue, setNestedValueImmutable } from '@/utils/nestedObjects'

type Vec3 = { x: number; y: number; z: number }
type OrbitControls = {
  target: THREE.Vector3
  enabled: boolean
  update: () => void
  addEventListener: (event: string, callback: () => void) => void
}

const DEBOUNCE_DELAY = 150
const createDebouncedMap = () => {
  let timers: Record<string, ReturnType<typeof setTimeout>> = {}
  return (key: string, callback: () => void) => {
    if (timers[key]) clearTimeout(timers[key])
    timers = {
      ...timers,
      [key]: setTimeout(() => {
        const { [key]: _completed, ...remaining } = timers
        timers = remaining
        callback()
      }, DEBOUNCE_DELAY)
    }
  }
}

interface TextureGroupDefinition {
  id: string
  name: string
  textures: { id: string; name: string; filename: string; url: string }[]
}

interface DefineSetupContext {
  ground: { mesh?: THREE.Mesh } | null
  scene: THREE.Scene
  camera: THREE.Camera
  world: import('@dimforge/rapier3d-compat').default.World
  getDelta: () => number
  clock: THREE.Clock
  animate: (options: {
    beforeTimeline?: () => void
    afterTimeline?: () => void
    timeline: ReturnType<typeof createTimelineManager>
  }) => void
}

export interface InitOptions {
  defineSetup?: (context: DefineSetupContext) => Promise<void> | void
  viewPanels?: Record<string, boolean>
  playMode?: boolean
  onProgress?: OnProgress
}

export interface TextureAreaDefinition {
  name: string
  schema: Record<string, unknown>
  initialData: Record<string, unknown>
  meshPrefix: string
  textures?: { id: string; name: string; filename: string; url: string }[]
}

const SPHERE_SEGMENTS = 32
const MAX_INSTANCES = 2000
const MIN_INSTANCES = 1
const HALF = 0.5

interface SceneReferences {
  threeScene: Ref<THREE.Scene | null>
  threeWorld: Ref<unknown>
  threeCamera: Ref<THREE.Camera | null>
  orbitReference: Ref<OrbitControls | null>
  groundReference: Ref<{ mesh?: THREE.Mesh } | null>
  ambientLightReference: Ref<THREE.Light | null>
  directionalLightReference: Ref<THREE.Light | null>
  skyMeshReference: Ref<THREE.Mesh | null>
  isInitialized: Ref<boolean>
  playMode: Ref<boolean>
  cameraConfig: Ref<Record<string, unknown>>
  groundConfig: Ref<Record<string, unknown>>
  lightsConfig: Ref<Record<string, unknown>>
  skyConfig: Ref<Record<string, unknown>>
  groupConfigs: Ref<Record<string, Record<string, unknown>>>
  textureAreaConfigs: Ref<Record<string, Record<string, unknown>>>
  textureAreaDefinitions: Ref<TextureAreaDefinition[]>
  areaMeshCache: Ref<Record<string, THREE.Object3D[]>>
}

// --- Config builders ---

const buildCameraConfigFromSetup = (config: SetupConfig): Record<string, unknown> => {
  const cameraPosition = (config.camera?.position ?? SCENE_DEFAULTS.camera.position) as number[]
  const orbitDisabledFromConfig =
    config.orbit &&
    typeof config.orbit === 'object' &&
    'disabled' in config.orbit &&
    config.orbit.disabled === true
  return {
    position: { x: cameraPosition[0], y: cameraPosition[1], z: cameraPosition[2] },
    fov: config.camera?.fov ?? SCENE_DEFAULTS.camera.fov,
    orbitTarget: { x: 0, y: 0, z: 0 },
    orbitEnabled: config.orbit !== false && !orbitDisabledFromConfig
  }
}

const buildGroundConfigFromSetup = (config: SetupConfig): Record<string, unknown> => {
  const groundOverrides = (
    config.ground && typeof config.ground === 'object' ? config.ground : {}
  ) as { color?: number; size?: number | number[] }
  const size = groundOverrides.size ?? SCENE_DEFAULTS.ground.size
  return {
    color: groundOverrides.color ?? SCENE_DEFAULTS.ground.color,
    size: Array.isArray(size)
      ? { x: size[0], y: size[1], z: size[2] }
      : { x: size ?? 100, y: 0.1, z: size ?? 100 }
  }
}

const DEFAULT_HEMISPHERE = { skyColor: 0xbfe3ff, groundColor: 0x8a8f7a }

/**
 * The panel keeps hemisphere colors as skyColor/groundColor while SetupConfig carries a
 * tuple. Always present so the panel can offer all four lights: a scene without one gets
 * it at zero intensity, which creates nothing and changes nothing until it is raised.
 * @param hemisphereOverrides The scene's hemisphere colours and intensity, if any.
 * @returns The panel's hemisphere entry.
 */
const buildHemisphereConfig = (
  hemisphereOverrides: { colors?: [number, number]; intensity?: number } | undefined
): Record<string, unknown> => {
  const colors = hemisphereOverrides?.colors
  return {
    hemisphere: {
      skyColor: colors?.[0] ?? DEFAULT_HEMISPHERE.skyColor,
      groundColor: colors?.[1] ?? DEFAULT_HEMISPHERE.groundColor,
      intensity: hemisphereOverrides?.intensity ?? (colors ? 1 : 0)
    }
  }
}

const buildLightsConfigFromSetup = (config: SetupConfig): Record<string, unknown> => {
  const lightsOverrides = (
    config.lights && typeof config.lights === 'object' ? config.lights : {}
  ) as Record<string, unknown>
  const ambientOverrides = (lightsOverrides.ambient ?? {}) as Record<string, unknown>
  const directionalOverrides = (lightsOverrides.directional ?? {}) as Record<string, unknown>
  const hemisphereOverrides = lightsOverrides.hemisphere as
    | { colors?: [number, number]; intensity?: number }
    | undefined
  const directionalPosition = (directionalOverrides.position ??
    SCENE_DEFAULTS.lights.directional.position) as number[]
  return {
    ambient: { ...SCENE_DEFAULTS.lights.ambient, ...ambientOverrides },
    directional: {
      ...SCENE_DEFAULTS.lights.directional,
      ...directionalOverrides,
      position: {
        x: directionalPosition[0],
        y: directionalPosition[1],
        z: directionalPosition[2]
      }
    },
    ...(lightsOverrides.environment && typeof lightsOverrides.environment === 'object'
      ? { environment: { ...(lightsOverrides.environment as Record<string, unknown>) } }
      : {}),
    ...buildHemisphereConfig(hemisphereOverrides)
  }
}

const buildSkyConfigFromSetup = (config: SetupConfig): Record<string, unknown> => {
  const skyOverrides = (config.sky && typeof config.sky === 'object' ? config.sky : {}) as Record<
    string,
    unknown
  >
  return { ...SCENE_DEFAULTS.sky, ...skyOverrides }
}

const buildInitConfig = (
  config: SetupConfig,
  configReferences: Pick<
    SceneReferences,
    'cameraConfig' | 'groundConfig' | 'lightsConfig' | 'skyConfig'
  >
) => {
  configReferences.cameraConfig.value = buildCameraConfigFromSetup(config)
  if (config.ground !== false)
    configReferences.groundConfig.value = buildGroundConfigFromSetup(config)
  if (config.lights !== false)
    configReferences.lightsConfig.value = buildLightsConfigFromSetup(config)
  if (config.sky !== false) configReferences.skyConfig.value = buildSkyConfigFromSetup(config)
}

// --- Scene object updaters ---

const applyGroundUpdate = (
  path: string,
  value: unknown,
  groundReference: Ref<{ mesh?: THREE.Mesh } | null>
) => {
  const ground = groundReference.value
  if (!ground?.mesh) return
  if (path === 'color') {
    ;(ground.mesh.material as THREE.MeshStandardMaterial).color.set(value as number)
  } else if (path === 'size') {
    const size = value as Vec3
    replaceGeometry(ground.mesh, new THREE.BoxGeometry(size.x, size.y, size.z))
  }
}

type LightPanelReferences = {
  lightsConfig: Ref<Record<string, unknown>>
  skyConfig: Ref<Record<string, unknown>>
  threeScene: Ref<THREE.Scene | null>
  activeLightPreset: Ref<LightPreset | null>
}

/**
 * Mirror an applied preset into the panel state so the light rows show what the preset
 * set rather than the values from before it. The panel keeps the directional position as
 * {x, y, z} and the hemisphere colors as skyColor/groundColor, while the preset carries
 * tuples.
 * @param current The panel's light state.
 * @param presetConfig The preset just applied.
 * @returns The panel state with the preset's values.
 */
const mirrorLightPresetIntoConfig = (
  current: Record<string, unknown>,
  presetConfig: (typeof lightPresets)[LightPreset]
): Record<string, unknown> => ({
  ...current,
  ambient: {
    ...(current.ambient as Record<string, unknown>),
    ...presetConfig.ambient
  },
  directional: {
    ...(current.directional as Record<string, unknown>),
    ...presetConfig.directional,
    ...(presetConfig.directional?.position
      ? {
          position: {
            x: presetConfig.directional.position[0],
            y: presetConfig.directional.position[1],
            z: presetConfig.directional.position[2]
          }
        }
      : {})
  },
  ...(presetConfig.hemisphere
    ? {
        hemisphere: {
          ...(current.hemisphere as Record<string, unknown>),
          ...(presetConfig.hemisphere.colors
            ? {
                skyColor: presetConfig.hemisphere.colors[0],
                groundColor: presetConfig.hemisphere.colors[1]
              }
            : {}),
          ...(presetConfig.hemisphere.intensity !== undefined
            ? { intensity: presetConfig.hemisphere.intensity }
            : {})
        }
      }
    : {}),
  ...(current.environment
    ? {
        environment: {
          ...(current.environment as Record<string, unknown>),
          ...presetConfig.environment
        }
      }
    : {})
})

/**
 * Hide or restore the whole rig as one: every light object plus the environment map,
 * which is not a scene child and so has to be stashed rather than made invisible.
 * @param scene The scene whose lights are toggled.
 * @param environmentStash The environment map stashed when the rig was hidden, if any.
 * @returns The environment map to keep stashed, or null once restored.
 */
const toggleLightRigVisibility = (
  scene: THREE.Scene,
  environmentStash: THREE.Texture | null
): THREE.Texture | null => {
  const rigObjects = [...LIGHT_ELEMENT_NAMES]
    .map((childName) => scene.getObjectByName(childName))
    .filter((object): object is THREE.Object3D => object !== undefined)
  const hiding = rigObjects.some((object) => object.visible) || !!scene.environment
  rigObjects
    .filter((object) => object.visible === hiding)
    .forEach((object) => toggleObjectVisibility(object))
  if (hiding) return scene.environment ? setSceneEnvironment(scene, null) : environmentStash
  return environmentStash ? setSceneEnvironment(scene, environmentStash) : null
}

/**
 * Take on a scene this store did not build, so a view that lit itself still reaches the
 * Lights element. Does nothing for a scene the store set up, which already has its config.
 * @param scene - The scene whose lights the panel should drive
 * @param references - Where the adopted scene and its light state are kept
 */
const adoptSceneLightsInto = (
  scene: THREE.Scene,
  references: Pick<LightPanelReferences, 'threeScene' | 'lightsConfig' | 'skyConfig'> & {
    isInitialized: Ref<boolean>
  }
): void => {
  if (references.isInitialized.value) return
  references.threeScene.value = scene
  const { lights, sky } = readLightsFromScene(scene)
  references.lightsConfig.value = lights
  references.skyConfig.value = sky
}

/**
 * Register the ground row, when the scene declared one.
 * @param references - The properties store, the panel state and the ground it drives
 */
const registerGroundElementProperties = (references: {
  elementPropertiesStore: ReturnType<typeof useElementPropertiesStore>
  groundConfig: Ref<Record<string, unknown>>
  groundReference: Ref<{ mesh?: THREE.Mesh } | null>
}): void => {
  const { elementPropertiesStore, groundConfig, groundReference } = references
  if (Object.keys(groundConfig.value).length === 0) return

  elementPropertiesStore.registerElementProperties('ground', {
    title: 'Ground',
    schema: groundSchema,
    getValue: (path) => getNestedValue(groundConfig.value, path),
    updateValue: (path, value) => {
      groundConfig.value = setNestedValueImmutable(groundConfig.value, path, value)
      applyGroundUpdate(path, value, groundReference)
    }
  })
}

const mirrorRigIntoPanelState = (
  rig: (typeof lightPresets)[LightPreset],
  references: Pick<LightPanelReferences, 'lightsConfig' | 'skyConfig'>
): void => {
  references.lightsConfig.value = mirrorLightPresetIntoConfig(references.lightsConfig.value, rig)
  if (rig.sky?.color !== undefined && Object.keys(references.skyConfig.value).length > 0)
    references.skyConfig.value = setNestedValueImmutable(
      references.skyConfig.value,
      'color',
      rig.sky.color
    )
}

const applyLightPresetToScene = (preset: LightPreset, references: LightPanelReferences): void => {
  const scene = references.threeScene.value
  if (!scene) return
  const presetConfig = lightPresets[preset]
  updateLights(scene, presetConfig)
  mirrorRigIntoPanelState(presetConfig, references)
  references.activeLightPreset.value = preset
}

export type LightGroup =
  | 'ambient'
  | 'directional'
  | 'hemisphere'
  | 'environment'
  | 'point'
  | 'spot'
  | 'rectArea'

/**
 * Translate one group of the panel's light state into the config `updateLights` takes:
 * the panel keeps the directional position as {x, y, z} and the hemisphere colors as
 * skyColor/groundColor, while the package expects tuples.
 * @param lightsConfig The panel's light state.
 * @returns The hemisphere update, colours as a tuple.
 */
const buildHemisphereUpdate = (
  lightsConfig: Record<string, unknown>
): Parameters<typeof updateLights>[1] => {
  const hemisphere = (lightsConfig.hemisphere ?? {}) as {
    skyColor?: number
    groundColor?: number
    intensity?: number
  }
  const hasColors = hemisphere.skyColor !== undefined && hemisphere.groundColor !== undefined
  return {
    hemisphere: {
      ...(hasColors
        ? { colors: [hemisphere.skyColor, hemisphere.groundColor] as [number, number] }
        : {}),
      ...(hemisphere.intensity !== undefined ? { intensity: hemisphere.intensity } : {})
    }
  }
}

const toTuple = ({ x, y, z }: Vec3): CoordinateTuple => [x, y, z]

const buildDirectionalUpdate = (
  lightsConfig: Record<string, unknown>
): Parameters<typeof updateLights>[1] => {
  const directional = (lightsConfig.directional ?? {}) as {
    color?: number
    intensity?: number
    position?: Vec3
  }
  const { position } = directional
  return {
    directional: {
      ...(directional.color !== undefined ? { color: directional.color } : {}),
      ...(directional.intensity !== undefined ? { intensity: directional.intensity } : {}),
      ...(position ? { position: toTuple(position) } : {})
    }
  }
}

const buildSpotUpdate = (
  lightsConfig: Record<string, unknown>
): Parameters<typeof updateLights>[1] => {
  const { position, target, ...spot } = (lightsConfig.spot ?? {}) as Omit<
    NonNullable<LightsConfig['spot']>,
    'position' | 'target'
  > & { position?: Vec3; target?: Vec3 }
  return {
    spot: {
      ...spot,
      ...(position ? { position: toTuple(position) } : {}),
      ...(target ? { target: toTuple(target) } : {})
    }
  }
}

const buildEnvironmentUpdate = (
  lightsConfig: Record<string, unknown>
): Parameters<typeof updateLights>[1] => {
  const environment = (lightsConfig.environment ?? {}) as { intensity?: number }
  return environment.intensity !== undefined
    ? { environment: { intensity: environment.intensity } }
    : {}
}

const LIGHT_GROUP_UPDATE_BUILDERS: Record<
  LightGroup,
  (lightsConfig: Record<string, unknown>) => Parameters<typeof updateLights>[1]
> = {
  hemisphere: buildHemisphereUpdate,
  directional: buildDirectionalUpdate,
  environment: buildEnvironmentUpdate,
  ambient: (lightsConfig) => ({
    ambient: (lightsConfig.ambient ?? {}) as { color?: number; intensity?: number }
  }),
  point: (lightsConfig) => ({ point: (lightsConfig.point ?? {}) as LightsConfig['point'] }),
  spot: buildSpotUpdate,
  rectArea: (lightsConfig) => ({
    rectArea: (lightsConfig.rectArea ?? {}) as LightsConfig['rectArea']
  })
}

const buildLightGroupUpdate = (
  group: LightGroup,
  lightsConfig: Record<string, unknown>
): Parameters<typeof updateLights>[1] => LIGHT_GROUP_UPDATE_BUILDERS[group](lightsConfig)

const applySkyUpdate = (path: string, value: unknown, skyMeshReference: Ref<THREE.Mesh | null>) => {
  const mesh = skyMeshReference.value
  if (!mesh) return
  if (path === 'color') {
    ;(mesh.material as THREE.MeshBasicMaterial).color.set(value as number)
  } else if (path === 'size') {
    replaceGeometry(
      mesh,
      new THREE.SphereGeometry(value as number, SPHERE_SEGMENTS, SPHERE_SEGMENTS)
    )
  }
}

// --- Scene element builders ---

const buildSceneElements = (
  threeScene: Ref<THREE.Scene | null>,
  threeCamera: Ref<THREE.Camera | null>,
  areaMeshCache: Ref<Record<string, THREE.Object3D[]>>,
  textureStore: ReturnType<typeof useTextureGroupsStore>
): SceneElement[] => {
  const scene = threeScene.value
  const camera = threeCamera.value
  if (!scene || !camera) return []

  const textureGroups = textureStore.groups
  const cachedMeshNames = new Set(
    Object.values(areaMeshCache.value).flatMap((meshes) => meshes.map((m) => m.name))
  )
  const cameraElement: SceneElement = { name: 'Camera', type: camera.type, hidden: false }

  const listedChildren = scene.children
    .filter((child) => !LIGHT_ELEMENT_NAMES.has(child.name))
    .filter((child) => !cachedMeshNames.has(child.name))
    .filter((child) => !child.userData?.spawnId)
    .filter((child) => !(child instanceof THREE.BoxHelper))
    .filter((child) => child.name !== 'PathDebug')

  // A scene names every column `column`, so without numbering each repeat selects the first.
  const childNames = numberDuplicateNames(listedChildren.map((child) => child.name || child.type))

  const sceneChildren: SceneElement[] = listedChildren.map((child, index) => {
    const groupId = textureGroups.find(
      (g) => child.name?.startsWith(`grp-${g.id}-`) || child.name === `wireframe-${g.id}`
    )?.id
    const childCount =
      child instanceof THREE.Group && child.children.length > 0 ? child.children.length : null
    return {
      name: childNames[index],
      label: childCount !== null ? `${childNames[index]} [${childCount}]` : undefined,
      type: groupId ? 'TextureArea' : child.type,
      hidden: false,
      groupId,
      objectId: child.uuid
    }
  })

  const textureGroupElements: SceneElement[] = textureGroups.map((g) => ({
    name: g.id,
    type: 'TextureArea',
    hidden: false,
    groupId: g.id
  }))

  // The whole rig lives on one row: the individual lights and the sky are filtered out of
  // the children above, and scene.environment is not a child at all.
  const hasLightRig =
    scene.children.some((child) => LIGHT_ELEMENT_NAMES.has(child.name)) || !!scene.environment
  const lightsElements: SceneElement[] = hasLightRig
    ? [{ name: 'Lights', type: 'Lights', hidden: false }]
    : []

  return [cameraElement, ...lightsElements, ...sceneChildren, ...textureGroupElements]
}

const buildAreaMeshCacheFromScene = (
  scene: THREE.Scene,
  textureAreaDefinitions: TextureAreaDefinition[]
): Record<string, THREE.Object3D[]> => {
  const prefixToName = Object.fromEntries(
    textureAreaDefinitions.map((area) => [`${area.meshPrefix}-`, area.name])
  )
  const prefixes = Object.keys(prefixToName)

  return scene.children.reduce<Record<string, THREE.Object3D[]>>(
    (cache, child) => {
      const matchedPrefix = child.name
        ? prefixes.find((prefix) => child.name.startsWith(prefix))
        : undefined
      if (matchedPrefix) {
        const areaName = prefixToName[matchedPrefix]
        return { ...cache, [areaName]: [...(cache[areaName] ?? []), child] }
      }
      return cache
    },
    Object.fromEntries(textureAreaDefinitions.map((area) => [area.name, [] as THREE.Object3D[]]))
  )
}

// --- Texture mesh placement ---

interface TextureMeshOptions {
  scene: THREE.Scene
  world: unknown
  area: TextureAreaDefinition
  position: CoordinateTuple
  index: number
  textures: { id: string; name: string; filename: string; url: string }[]
  meshSize: CoordinateTuple
  meshRotation: CoordinateTuple
}

const placeTextureMesh = (meshOptions: TextureMeshOptions): THREE.Object3D =>
  getCube(meshOptions.scene, meshOptions.world, {
    name: `${meshOptions.area.meshPrefix}-${meshOptions.index}`,
    size: meshOptions.meshSize,
    position: meshOptions.position,
    rotation: meshOptions.meshRotation,
    texture: meshOptions.textures[Math.floor(Math.random() * meshOptions.textures.length)].url,
    material: 'MeshBasicMaterial',
    color: 0x000000,
    transparent: true,
    castShadow: false,
    receiveShadow: false,
    physic: false
  }) as unknown as THREE.Object3D

const computeMeshTransform = (
  baseSize: CoordinateTuple,
  rotation: CoordinateTuple,
  sizeVariation: CoordinateTuple,
  rotationVariation: CoordinateTuple,
  hasVariation: boolean
): { meshSize: CoordinateTuple; meshRotation: CoordinateTuple } => {
  if (!hasVariation) {
    return { meshSize: baseSize, meshRotation: rotation }
  }
  return {
    meshSize: [
      baseSize[0] + (Math.random() - HALF) * sizeVariation[0],
      baseSize[1] + (Math.random() - HALF) * sizeVariation[1],
      baseSize[2] + (Math.random() - HALF) * sizeVariation[2]
    ],
    meshRotation: [
      rotation[0] + (Math.random() - HALF) * rotationVariation[0],
      rotation[1] + (Math.random() - HALF) * rotationVariation[1],
      rotation[2] + (Math.random() - HALF) * rotationVariation[2]
    ]
  }
}

const populateTextureArea = (
  scene: THREE.Scene,
  world: unknown,
  area: TextureAreaDefinition,
  config: GroupConfig,
  textures: { id: string; name: string; filename: string; url: string }[]
): THREE.Object3D[] => {
  const areaConfig: AreaConfig = {
    center: config.area.center,
    size: config.area.size,
    count: Math.min(MAX_INSTANCES, Math.max(MIN_INSTANCES, Math.round(config.instances.density))),
    pattern: config.instances.pattern,
    seed: config.instances.seed,
    sizeVariation: config.textures.sizeVariation,
    rotationVariation: config.textures.rotationVariation
  }

  const allPositions = generateAreaPositions(areaConfig)
  const {
    baseSize,
    rotation = [0, 0, 0] as CoordinateTuple,
    sizeVariation,
    rotationVariation
  } = config.textures
  const hasVariation = sizeVariation.some((v) => v !== 0) || rotationVariation.some((v) => v !== 0)

  return allPositions.map((position: CoordinateTuple, index: number) => {
    const { meshSize, meshRotation } = computeMeshTransform(
      baseSize,
      rotation,
      sizeVariation,
      rotationVariation,
      hasVariation
    )
    return placeTextureMesh({
      scene,
      world,
      area,
      position,
      index,
      textures,
      meshSize,
      meshRotation
    })
  })
}

// --- Setup helpers ---

interface SetupFunction {
  (options: {
    config: SetupConfig
    defineSetup?: (ctx: unknown) => Promise<void> | void
  }): Promise<{ ground: { mesh?: THREE.Mesh } | null; orbit: unknown }>
}

interface AnimateFunction {
  (options: {
    beforeTimeline?: () => void
    afterTimeline?: () => void
    timeline: ReturnType<typeof createTimelineManager>
  }): void
}

interface DefineSetupRunOptions {
  setup: SetupFunction
  toolsAnimate: AnimateFunction
  scene: THREE.Scene
  camera: THREE.Camera
  world: import('@dimforge/rapier3d-compat').default.World
  getDelta: () => number
  clock: THREE.Clock
  config: SetupConfig
  defineSetupCallback: (context: DefineSetupContext) => Promise<void> | void
  cleanup: () => void
  setActiveCamera: (newCamera: THREE.Camera) => OrbitControls | null
}

const runSetupWithDefineSetup = async (
  runOptions: DefineSetupRunOptions
): Promise<SceneSetupResult> => {
  const {
    setup,
    toolsAnimate,
    scene,
    camera,
    world,
    getDelta,
    clock,
    config,
    defineSetupCallback,
    cleanup,
    setActiveCamera
  } = runOptions
  const result = await setup({
    config,
    defineSetup: async (setupContext) => {
      await defineSetupCallback({
        ground: (setupContext as { ground: { mesh?: THREE.Mesh } | null }).ground,
        scene,
        camera,
        world,
        getDelta,
        clock,
        animate: toolsAnimate
      })
    }
  })
  return {
    ground: result.ground,
    orbit: result.orbit as OrbitControls,
    scene,
    camera,
    world,
    cleanup,
    setActiveCamera
  }
}

interface SceneSetupResult {
  scene: THREE.Scene
  camera: THREE.Camera
  world: import('@dimforge/rapier3d-compat').default.World
  /** Null for a scene set up with `orbit: false`, which has no orbit controls at all. */
  orbit: OrbitControls | null
  ground: { mesh?: THREE.Mesh } | null
  cleanup: () => void
  /** Swaps the render camera and returns the orbit controls now bound to it. */
  setActiveCamera: (newCamera: THREE.Camera) => OrbitControls | null
}

const resolveSceneReferencesFromResult = (
  setupResult: SceneSetupResult,
  sceneReferences: Pick<
    SceneReferences,
    | 'threeScene'
    | 'threeCamera'
    | 'threeWorld'
    | 'orbitReference'
    | 'groundReference'
    | 'ambientLightReference'
    | 'directionalLightReference'
    | 'skyMeshReference'
  >
) => {
  const { scene, camera, world, orbit, ground } = setupResult
  sceneReferences.threeScene.value = scene
  sceneReferences.threeCamera.value = camera
  sceneReferences.threeWorld.value = world
  sceneReferences.orbitReference.value = orbit
  sceneReferences.groundReference.value = ground
  sceneReferences.ambientLightReference.value =
    (scene.getObjectByName('ambient-light') as THREE.Light) ?? null
  sceneReferences.directionalLightReference.value =
    (scene.getObjectByName('directional-light') as THREE.Light) ?? null
  sceneReferences.skyMeshReference.value = (scene.getObjectByName('sky') as THREE.Mesh) ?? null
}

// --- Wireframe helpers ---

const toggleAreaWireframe = (
  id: string,
  references: Pick<SceneReferences, 'threeScene' | 'threeWorld' | 'textureAreaConfigs'>,
  textureStore: ReturnType<typeof useTextureGroupsStore>
) => {
  const scene = references.threeScene.value
  const world = references.threeWorld.value
  const group = textureStore.groups.find((g) => g.id === id)
  const config = references.textureAreaConfigs.value[id] as unknown as GroupConfig
  if (!group || !scene || !config?.area) return

  const newShowWireframe = !group.showWireframe
  textureStore.$patch({
    groups: textureStore.groups.map((g) =>
      g.id === id ? { ...g, showWireframe: newShowWireframe } : g
    )
  })

  const wireframeName = `wireframe-${id}`
  const existing = scene.getObjectByName(wireframeName)
  if (existing) scene.remove(existing)

  if (newShowWireframe) {
    getCube(scene, world, {
      name: wireframeName,
      size: config.area.size,
      position: config.area.center,
      color: 0x00ff00,
      material: 'MeshBasicMaterial',
      wireframe: true,
      castShadow: false,
      receiveShadow: false,
      physic: false
    })
  }
}

// --- Texture area group handlers ---

interface TextureAreaHandlerContext {
  references: Pick<
    SceneReferences,
    'threeScene' | 'threeWorld' | 'areaMeshCache' | 'textureAreaConfigs' | 'textureAreaDefinitions'
  >
  textureStore: ReturnType<typeof useTextureGroupsStore>
  elementPropertiesStore: ReturnType<typeof useElementPropertiesStore>
  getAreaMeshes: (name: string) => THREE.Object3D[]
  regenerateTextureArea: (area: TextureAreaDefinition) => void
  updateSceneElements: () => void
}

const buildGroupHandlersForTextureAreas = (context: TextureAreaHandlerContext) => {
  const {
    references,
    textureStore,
    elementPropertiesStore,
    getAreaMeshes,
    regenerateTextureArea,
    updateSceneElements
  } = context
  return {
    onSelectGroup: (id: string) => {
      elementPropertiesStore.openElementProperties(id)
    },
    onRemoveGroup: (id: string) => {
      const scene = references.threeScene.value
      if (scene) {
        getAreaMeshes(id).forEach((mesh) => scene.remove(mesh))
      }
      const { [id]: _removed, ...remainingCache } = references.areaMeshCache.value
      references.areaMeshCache.value = remainingCache
      textureStore.$patch({
        groups: textureStore.groups.filter((g) => g.id !== id)
      })
      references.textureAreaDefinitions.value = references.textureAreaDefinitions.value.filter(
        (a) => a.name !== id
      )
      elementPropertiesStore.unregisterElementProperties(id)
      updateSceneElements()
    },
    onRemoveTexture: () => {},
    onToggleVisibility: (id: string) => {
      const group = textureStore.groups.find((g) => g.id === id)
      if (!group) return
      textureStore.$patch({
        groups: textureStore.groups.map((g) => (g.id === id ? { ...g, hidden: !group.hidden } : g))
      })
      getAreaMeshes(id).forEach((child) => toggleObjectVisibility(child))
    },
    onToggleWireframe: (id: string) => {
      toggleAreaWireframe(
        id,
        {
          threeScene: references.threeScene,
          threeWorld: references.threeWorld,
          textureAreaConfigs: references.textureAreaConfigs
        },
        textureStore
      )
    },
    onAddTextureToGroup: () => {},
    onAddNewGroup: () => {},
    onManualUpdate: () => {
      references.textureAreaDefinitions.value.forEach((area) => regenerateTextureArea(area))
    },
    onAddElement: () => {}
  }
}

// --- Texture group handlers ---

interface TextureGroupHandlerContext {
  references: Pick<SceneReferences, 'threeScene' | 'threeWorld' | 'groupConfigs'>
  textureStore: ReturnType<typeof useTextureGroupsStore>
  elementPropertiesStore: ReturnType<typeof useElementPropertiesStore>
  getGroupConfig: (groupId: string) => GroupConfig
  registerGroupProperties: (group: TextureGroup) => void
  regenerateGroup: (id: string) => void
  updateSceneElements: () => void
}

const addNewTextureGroup = (event: Event, context: TextureGroupHandlerContext) => {
  const {
    references,
    textureStore,
    elementPropertiesStore,
    getGroupConfig,
    registerGroupProperties,
    updateSceneElements
  } = context
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files || files.length === 0) return

  const file = files[0]
  const url = URL.createObjectURL(file)
  const filename = file.name.replace(/\.[^./]+$/, '')
  const textureId = `texture-${Date.now()}`
  const groupId = `group-${Date.now()}`

  const newGroup: TextureGroup = {
    id: groupId,
    name: filename,
    textures: [{ id: textureId, name: filename, filename, url }]
  }

  const defaultConfig = textureStore.createDefaultGroupConfig()
  references.groupConfigs.value = {
    ...references.groupConfigs.value,
    [groupId]: defaultConfig as unknown as Record<string, unknown>
  }

  textureStore.$patch({ groups: [...textureStore.groups, newGroup] })
  registerGroupProperties(newGroup)
  elementPropertiesStore.openElementProperties(groupId)

  const scene = references.threeScene.value
  if (scene) addGroupMeshes(scene, references.threeWorld.value, newGroup, getGroupConfig)
  updateSceneElements()
  target.value = ''
}

const addTextureToExistingGroup = (
  groupId: string,
  event: Event,
  textureStore: ReturnType<typeof useTextureGroupsStore>,
  regenerateGroup: (id: string) => void
) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files || files.length === 0) return

  const file = files[0]
  const url = URL.createObjectURL(file)
  const filename = file.name.replace(/\.[^./]+$/, '')
  const textureId = `texture-${Date.now()}`

  const group = textureStore.groups.find((g) => g.id === groupId)
  if (group) {
    textureStore.$patch({
      groups: textureStore.groups.map((g) =>
        g.id === groupId
          ? { ...g, textures: [...g.textures, { id: textureId, name: filename, filename, url }] }
          : g
      )
    })
    regenerateGroup(groupId)
  }
  target.value = ''
}

const buildGroupHandlersForTextureGroups = (context: TextureGroupHandlerContext) => {
  const { references, textureStore, elementPropertiesStore, regenerateGroup, updateSceneElements } =
    context
  return {
    onSelectGroup: (id: string) => {
      elementPropertiesStore.openElementProperties(id)
    },
    onRemoveGroup: (id: string) => {
      const scene = references.threeScene.value
      if (scene) removeGroupMeshes(scene, id)
      textureStore.$patch({ groups: textureStore.groups.filter((g) => g.id !== id) })
      elementPropertiesStore.unregisterElementProperties(id)
      updateSceneElements()
    },
    onRemoveTexture: () => {},
    onToggleVisibility: (id: string) => {
      const scene = references.threeScene.value
      const group = textureStore.groups.find((g) => g.id === id)
      if (!group) return
      textureStore.$patch({
        groups: textureStore.groups.map((g) => (g.id === id ? { ...g, hidden: !group.hidden } : g))
      })
      if (scene) {
        const prefix = `grp-${id}-`
        scene.children
          .filter((child) => child.name?.startsWith(prefix))
          .forEach((child) => toggleObjectVisibility(child))
      }
    },
    onToggleWireframe: (id: string) => {
      const group = textureStore.groups.find((g) => g.id === id)
      if (group) {
        textureStore.$patch({
          groups: textureStore.groups.map((g) =>
            g.id === id ? { ...g, showWireframe: !g.showWireframe } : g
          )
        })
        regenerateGroup(id)
      }
    },
    onAddTextureToGroup: (groupId: string, event: Event) => {
      addTextureToExistingGroup(groupId, event, textureStore, regenerateGroup)
    },
    onAddNewGroup: (event: Event) => {
      addNewTextureGroup(event, context)
    },
    onManualUpdate: () => {
      textureStore.groups.forEach((group) => regenerateGroup(group.id))
    },
    onAddElement: () => {}
  }
}

const mountGroupMeshes = (
  scene: THREE.Scene,
  world: unknown,
  textureStore: ReturnType<typeof useTextureGroupsStore>,
  getGroupConfig: (groupId: string) => GroupConfig,
  registerGroupProperties: (group: TextureGroup) => void
) => {
  textureStore.groups.forEach((group) => {
    addGroupMeshes(scene, world, group, getGroupConfig)
    registerGroupProperties(group)
  })
}

// --- Scene element removal ---

interface RemoveHandlerContext {
  threeScene: Ref<THREE.Scene | null>
  threeWorld: Ref<unknown>
  areaMeshCache: Ref<Record<string, THREE.Object3D[]>>
  textureAreaDefinitions: Ref<TextureAreaDefinition[]>
  elementPropertiesStore: ReturnType<typeof useElementPropertiesStore>
  getAreaMeshes: (name: string) => THREE.Object3D[]
  findObject: (name: string) => THREE.Object3D | undefined
  updateSceneElements: () => void
}

const removeSceneElement = (name: string, context: RemoveHandlerContext) => {
  const {
    threeScene,
    threeWorld,
    areaMeshCache,
    textureAreaDefinitions,
    elementPropertiesStore,
    getAreaMeshes
  } = context
  const scene = threeScene.value
  const world = threeWorld.value as { removeRigidBody: (body: unknown) => void } | null
  if (!scene) return

  const removePhysicsBodies = (object: THREE.Object3D) => {
    if (!world) return
    object.traverse((child) => {
      if ((child as THREE.Mesh).userData?.body) {
        world.removeRigidBody((child as THREE.Mesh).userData.body)
      }
    })
  }

  const cachedMeshes = getAreaMeshes(name)
  if (cachedMeshes.length > 0) {
    cachedMeshes.forEach((mesh) => {
      removePhysicsBodies(mesh)
      mesh.removeFromParent()
    })
    const { [name]: _removed, ...remainingCache } = areaMeshCache.value
    areaMeshCache.value = remainingCache
    textureAreaDefinitions.value = textureAreaDefinitions.value.filter((a) => a.name !== name)
    elementPropertiesStore.unregisterElementProperties(name)
  } else {
    const object = context.findObject(name)
    if (object) {
      removePhysicsBodies(object)
      object.removeFromParent()
    }
  }
  context.updateSceneElements()
}

// --- Store ---

export const useSceneViewStore = defineStore('sceneView', () => {
  const cameraConfig = ref<Record<string, unknown>>({})
  const groundConfig = ref<Record<string, unknown>>({})
  const lightsConfig = ref<Record<string, unknown>>({})
  const skyConfig = ref<Record<string, unknown>>({})
  const groupConfigs = ref<Record<string, Record<string, unknown>>>({})

  const threeScene = ref<THREE.Scene | null>(null)
  const threeWorld = ref<unknown>(null)
  const threeCamera = ref<THREE.Camera | null>(null)
  const orbitReference = ref<OrbitControls | null>(null)
  const groundReference = ref<{ mesh?: THREE.Mesh } | null>(null)
  const ambientLightReference = ref<THREE.Light | null>(null)
  const directionalLightReference = ref<THREE.Light | null>(null)
  const skyMeshReference = ref<THREE.Mesh | null>(null)
  const isInitialized = ref(false)
  const playMode = ref(false)
  const swapActiveCamera = ref<((newCamera: THREE.Camera) => OrbitControls | null) | null>(null)
  const textureAreaConfigs = ref<Record<string, Record<string, unknown>>>({})
  const textureAreaDefinitions = ref<TextureAreaDefinition[]>([])
  const areaMeshCache = ref<Record<string, THREE.Object3D[]>>({})

  let activeToolsCleanup: (() => void) | null = null
  let environmentTextureStash: THREE.Texture | null = null

  const panelsStore = usePanelsStore()
  const viewPanelsStore = useViewPanelsStore()
  const debugSceneStore = useDebugSceneStore()
  const elementPropertiesStore = useElementPropertiesStore()
  const textureStore = useTextureGroupsStore()
  const debouncedRegenerate = createDebouncedMap()

  const getAreaMeshes = (areaName: string): THREE.Object3D[] => areaMeshCache.value[areaName] ?? []

  /**
   * Resolve a panel row to its object: a numbered duplicate carries the uuid of its own.
   * @param scene The scene to search.
   * @param name The panel row's element name.
   * @returns The object, or undefined when it is gone.
   */
  const findSceneObject = (scene: THREE.Scene, name: string): THREE.Object3D | undefined => {
    const objectId = debugSceneStore.sceneElements.find(
      (element) => element.name === name
    )?.objectId
    return objectId
      ? (scene.getObjectByProperty('uuid', objectId) as THREE.Object3D | undefined)
      : scene.getObjectByName(name)
  }

  const buildAreaMeshCache = () => {
    const scene = threeScene.value
    if (!scene) return
    areaMeshCache.value = buildAreaMeshCacheFromScene(scene, textureAreaDefinitions.value)
  }

  const updateSceneElements = () => {
    const defaultGroups = Object.fromEntries(textureStore.groups.map((g) => [g.id, g.name]))

    const toggleVisibilityHandler = (name: string) => {
      const scene = threeScene.value
      const camera = threeCamera.value
      if (!scene) return
      const cachedMeshes = getAreaMeshes(name)
      if (name === 'Lights') {
        environmentTextureStash = toggleLightRigVisibility(scene, environmentTextureStash)
      } else if (cachedMeshes.length > 0) {
        cachedMeshes.forEach((child) => toggleObjectVisibility(child))
      } else {
        const object =
          name === 'Camera' ? (camera as unknown as THREE.Object3D) : findSceneObject(scene, name)
        if (object) toggleObjectVisibility(object)
      }
      debugSceneStore.$patch({
        sceneElements: debugSceneStore.sceneElements.map((element) =>
          element.name === name ? { ...element, hidden: !element.hidden } : element
        )
      })
    }

    const removeHandler = (name: string) =>
      removeSceneElement(name, {
        threeScene,
        threeWorld,
        areaMeshCache,
        textureAreaDefinitions,
        elementPropertiesStore,
        getAreaMeshes,
        findObject: (objectName: string) => {
          const scene = threeScene.value
          return scene ? findSceneObject(scene, objectName) : undefined
        },
        updateSceneElements
      })

    debugSceneStore.setSceneElements(
      buildSceneElements(threeScene, threeCamera, areaMeshCache, textureStore),
      { onToggleVisibility: toggleVisibilityHandler, onRemove: removeHandler },
      defaultGroups
    )
  }

  const registerCameraProperties = () => {
    const camera = threeCamera.value
    const orbit = orbitReference.value
    if (!camera || !('fov' in camera)) return

    const swap = swapActiveCamera.value

    registerCameraPropertiesShared({
      camera: camera as THREE.PerspectiveCamera,
      orbit,
      cameraConfig,
      skipOrbitSync: playMode.value,
      // Without this the panel reports only the current projection as supported, so the
      // orthographic camera and every orthographic preset are unreachable from the UI.
      setCamera: swap
        ? (newCamera: THREE.Camera) => {
            const nextOrbit = swap(newCamera)
            threeCamera.value = newCamera
            // The row's type was read off the camera when the list was built, so without this
            // an orthographic camera still calls itself a PerspectiveCamera in the panel.
            debugSceneStore.updateSceneElementType('Camera', newCamera.type)
            orbitReference.value = nextOrbit
            return nextOrbit
          }
        : undefined
    })
  }

  const registerGroundProperties = () =>
    registerGroundElementProperties({ elementPropertiesStore, groundConfig, groundReference })

  const activeLightPreset = ref<LightPreset | null>(null)

  const lightTransition = createLightTransitionPlayer({
    threeScene,
    onStart: () => {
      activeLightPreset.value = null
    },
    onStop: (rig) => mirrorRigIntoPanelState(rig, { lightsConfig, skyConfig })
  })

  const adoptSceneLights = (scene: THREE.Scene) =>
    adoptSceneLightsInto(scene, { threeScene, lightsConfig, skyConfig, isInitialized })

  const applyLightPreset = (preset: LightPreset) => {
    lightTransition.setEnabled(false)
    applyLightPresetToScene(preset, { lightsConfig, skyConfig, threeScene, activeLightPreset })
  }

  const updateLightValue = (group: LightGroup, path: string, value: unknown) => {
    lightsConfig.value = setNestedValueImmutable(lightsConfig.value, `${group}.${path}`, value)
    activeLightPreset.value = null
    const scene = threeScene.value
    if (!scene) return
    updateLights(scene, buildLightGroupUpdate(group, lightsConfig.value))
  }

  const updateSkyValue = (path: string, value: unknown) => {
    skyConfig.value = setNestedValueImmutable(skyConfig.value, path, value)
    applySkyUpdate(path, value, skyMeshReference)
  }

  const getGroupConfig = (groupId: string): GroupConfig => {
    const config = groupConfigs.value[groupId] as unknown as GroupConfig | undefined
    return config ?? textureStore.createDefaultGroupConfig()
  }

  const regenerateGroup = (groupId: string) => {
    const scene = threeScene.value
    const group = textureStore.groups.find((g) => g.id === groupId)
    if (!scene || !group) return
    removeGroupMeshes(scene, groupId)
    addGroupMeshes(scene, threeWorld.value, group, getGroupConfig)
  }

  const registerGroupProperties = (group: TextureGroup) => {
    const config = groupConfigs.value[group.id]
    if (!config) return

    elementPropertiesStore.registerElementProperties(group.id, {
      title: group.name,
      type: 'group',
      schema: configControls,
      getValue: (path) => getNestedValue(groupConfigs.value[group.id], path),
      updateValue: (path, value) => {
        groupConfigs.value = {
          ...groupConfigs.value,
          [group.id]: setNestedValueImmutable(groupConfigs.value[group.id], path, value)
        }
        if (textureStore.autoUpdate) {
          debouncedRegenerate(group.id, () => regenerateGroup(group.id))
        }
      }
    })
  }

  const getAreaResources = (areaName: string) => {
    const config = textureAreaConfigs.value[areaName] as unknown as GroupConfig
    if (!config?.area || !config?.textures || !config?.instances) return null
    const group = textureStore.groups.find((g) => g.id === areaName)
    const textures = group?.textures ?? []
    if (textures.length === 0) return null
    return { config, textures }
  }

  const regenerateTextureArea = (area: TextureAreaDefinition) => {
    const scene = threeScene.value
    const world = threeWorld.value
    if (!scene) return

    getAreaMeshes(area.name).forEach((mesh) => scene.remove(mesh))

    const resources = getAreaResources(area.name)
    if (!resources) return

    const newMeshes = populateTextureArea(scene, world, area, resources.config, resources.textures)
    areaMeshCache.value = { ...areaMeshCache.value, [area.name]: newMeshes }
  }

  const registerTextureAreaProperties = (areas: TextureAreaDefinition[]) => {
    areas.forEach((area) => {
      elementPropertiesStore.registerElementProperties(area.name, {
        title: area.name,
        type: 'TextureArea',
        schema: configControls,
        getValue: (path) => getNestedValue(textureAreaConfigs.value[area.name], path),
        updateValue: (path, value) => {
          textureAreaConfigs.value = {
            ...textureAreaConfigs.value,
            [area.name]: setNestedValueImmutable(textureAreaConfigs.value[area.name], path, value)
          }
          if (textureStore.autoUpdate) {
            debouncedRegenerate(area.name, () => regenerateTextureArea(area))
          }
        }
      })
    })
  }

  const registerTextureAreas = (areas: TextureAreaDefinition[]) => {
    textureAreaDefinitions.value = [...textureAreaDefinitions.value, ...areas]

    textureAreaConfigs.value = areas.reduce(
      (accumulator, area) => ({ ...accumulator, [area.name]: { ...area.initialData } }),
      { ...textureAreaConfigs.value }
    )

    textureStore.$patch({
      groups: [
        ...textureStore.groups,
        ...areas.map((area) => ({ id: area.name, name: area.name, textures: area.textures ?? [] }))
      ]
    })

    const areaReferences = {
      threeScene,
      threeWorld,
      areaMeshCache,
      textureAreaConfigs,
      textureAreaDefinitions
    }
    textureStore.registerHandlers(
      buildGroupHandlersForTextureAreas({
        references: areaReferences,
        textureStore,
        elementPropertiesStore,
        getAreaMeshes,
        regenerateTextureArea,
        updateSceneElements
      })
    )

    registerTextureAreaProperties(areas)

    const areaGroups = Object.fromEntries(areas.map((a) => [a.name, a.name]))
    debugSceneStore.$patch({ sceneGroups: { ...debugSceneStore.sceneGroups, ...areaGroups } })

    buildAreaMeshCache()
    updateSceneElements()
  }

  const unregisterTextureArea = (areaName: string) => {
    const scene = threeScene.value
    if (scene) {
      getAreaMeshes(areaName).forEach((mesh) => scene.remove(mesh))
    }
    const { [areaName]: _removed, ...remainingCache } = areaMeshCache.value
    areaMeshCache.value = remainingCache
    const { [areaName]: _removedConfig, ...remainingConfigs } = textureAreaConfigs.value
    textureAreaConfigs.value = remainingConfigs
    textureStore.$patch({
      groups: textureStore.groups.filter((g) => g.id !== areaName)
    })
    textureAreaDefinitions.value = textureAreaDefinitions.value.filter((a) => a.name !== areaName)
    elementPropertiesStore.unregisterElementProperties(areaName)
    updateSceneElements()
  }

  const runInit = async (
    canvas: HTMLCanvasElement,
    config: SetupConfig,
    options: InitOptions
  ): Promise<SceneSetupResult> => {
    const {
      setup,
      animate: toolsAnimate,
      scene,
      camera,
      world,
      getDelta,
      clock,
      cleanup: toolsCleanup,
      setActiveCamera
    } = await getTools({
      canvas,
      onProgress: options.onProgress
    })

    if (options.defineSetup) {
      return runSetupWithDefineSetup({
        setup,
        toolsAnimate,
        scene,
        camera,
        world,
        getDelta,
        clock,
        config,
        defineSetupCallback: options.defineSetup,
        cleanup: toolsCleanup,
        setActiveCamera
      })
    }

    const result = await setup({ config })
    toolsAnimate({ timeline: createTimelineManager() })
    return {
      ground: result.ground,
      orbit: result.orbit as OrbitControls,
      scene,
      camera,
      world,
      cleanup: toolsCleanup,
      setActiveCamera
    }
  }

  const init = async (canvas: HTMLCanvasElement, config: SetupConfig, options?: InitOptions) => {
    const resolvedOptions = options ?? {}
    playMode.value = resolvedOptions.playMode ?? false
    buildInitConfig(config, { cameraConfig, groundConfig, lightsConfig, skyConfig })

    const {
      ground,
      orbit,
      scene,
      camera,
      world,
      cleanup: toolsCleanup,
      setActiveCamera
    } = await runInit(canvas, config, resolvedOptions)
    activeToolsCleanup = toolsCleanup
    swapActiveCamera.value = setActiveCamera

    resolveSceneReferencesFromResult(
      { scene, camera, world, orbit: orbit ?? null, ground },
      {
        threeScene,
        threeCamera,
        threeWorld,
        orbitReference,
        groundReference,
        ambientLightReference,
        directionalLightReference,
        skyMeshReference
      }
    )

    registerCameraProperties()
    if (config.ground !== false) registerGroundProperties()
    // Started here rather than defaulted on in the player, which cannot run before there is a
    // scene to light. Opt-in: a game keeps the sky it declared.
    if (config.lights && config.lights.cycle) lightTransition.setEnabled(true)

    viewPanelsStore.setViewPanels(resolvedOptions.viewPanels ?? {})
    updateSceneElements()
    if (viewPanelsStore.viewPanels.showElements) panelsStore.openPanel('elements')
    isInitialized.value = true
  }

  const initTextureGroups = (
    groups: TextureGroupDefinition[],
    defaultGroups: Record<string, string>,
    initialConfigs?: Record<string, GroupConfig>
  ) => {
    groupConfigs.value = groups.reduce(
      (accumulator, group) => ({
        ...accumulator,
        [group.id]: (groupConfigs.value[group.id] ??
          (initialConfigs?.[group.id] as unknown as Record<string, unknown>) ??
          textureStore.createDefaultGroupConfig()) as unknown as Record<string, unknown>
      }),
      { ...groupConfigs.value }
    )

    textureStore.$patch({
      groups: [
        ...textureStore.groups,
        ...groups.map((g) => ({ id: g.id, name: g.name, textures: [...g.textures] }))
      ]
    })

    const groupReferences = { threeScene, threeWorld, groupConfigs }
    textureStore.registerHandlers(
      buildGroupHandlersForTextureGroups({
        references: groupReferences,
        textureStore,
        elementPropertiesStore,
        getGroupConfig,
        registerGroupProperties,
        regenerateGroup,
        updateSceneElements
      })
    )

    const scene = threeScene.value
    const world = threeWorld.value
    if (scene) mountGroupMeshes(scene, world, textureStore, getGroupConfig, registerGroupProperties)

    debugSceneStore.$patch({ sceneGroups: defaultGroups })
    updateSceneElements()
  }

  const cleanup = () => {
    activeToolsCleanup?.()
    activeToolsCleanup = null
    lightTransition.stop()
    activeLightPreset.value = null
    viewPanelsStore.clearViewPanels()
    debugSceneStore.clearSceneElements()
    elementPropertiesStore.clearAllElementProperties()
    textureStore.clear()
    cameraConfig.value = {}
    groundConfig.value = {}
    lightsConfig.value = {}
    skyConfig.value = {}
    groupConfigs.value = {}
    textureAreaConfigs.value = {}
    textureAreaDefinitions.value = []
    areaMeshCache.value = {}
    playMode.value = false
    threeScene.value = null
    threeWorld.value = null
    threeCamera.value = null
    swapActiveCamera.value = null
    orbitReference.value = null
    groundReference.value = null
    ambientLightReference.value = null
    directionalLightReference.value = null
    skyMeshReference.value = null
    isInitialized.value = false
  }

  return {
    cameraConfig,
    groundConfig,
    lightsConfig,
    skyConfig,
    groupConfigs,
    textureAreaConfigs,
    textureAreaDefinitions,
    threeScene,
    threeWorld,
    threeCamera,
    orbitReference,
    isInitialized,
    playMode,
    activeLightPreset,
    adoptSceneLights,
    lightTransitionEnabled: lightTransition.enabled,
    lightTransitionSpeed: lightTransition.speed,
    applyLightPreset,
    updateLightValue,
    updateSkyValue,
    setLightTransitionEnabled: lightTransition.setEnabled,
    setLightTransitionSpeed: lightTransition.setSpeed,
    init,
    initTextureGroups,
    registerTextureAreas,
    unregisterTextureArea,
    cleanup,
    getGroupConfig,
    regenerateGroup,
    refreshElements: updateSceneElements
  }
})
